/**
 * @description Super Admin switch-into overlay context (LYN-157)
 *
 * Key features:
 * - Loads current overlay from GET /api/switch
 * - start / stop / up — actor stays Super Admin
 * - Banner copy: You · Super Admin → {CP} → {SO}
 *
 * @dependencies
 * - adminApi: getSwitchOverlay, startSwitchOverlay, stopSwitchOverlay, upSwitchOverlay
 *
 * @notes
 * - Stay on admin.lynx.build. Exit clears overlay. Up retargets parent.
 * - Real logout is AuthContext.signOut (SA Wristband), not overlay stop.
 * - CP admin chrome is later (LYN-171); this context is SA-only UI.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  getSwitchOverlay,
  startSwitchOverlay,
  stopSwitchOverlay,
  upSwitchOverlay,
} from '../services/api/adminApi';

const OverlayContext = createContext(null);

export function formatOverlayBanner(overlay) {
  if (!overlay) return null;
  const labels = overlay.labels || {};
  const parts = ['You', 'Super Admin'];
  if (labels.channelPartner) parts.push(labels.channelPartner);
  if (labels.salesOrg) parts.push(labels.salesOrg);
  if (labels.merchant) parts.push(labels.merchant);
  return parts.join(' → ').replace('You → Super Admin', 'You · Super Admin');
}

export function OverlayProvider({ children }) {
  const [overlay, setOverlay] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const result = await getSwitchOverlay();
      setOverlay(result.data?.overlay || null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error?.message || null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const start = useCallback(async (kind, id) => {
    setBusy(true);
    try {
      const result = await startSwitchOverlay({ kind, id });
      setOverlay(result.data?.overlay || null);
      setError(null);
      return result;
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Switch-into failed';
      setError(message);
      throw err;
    } finally {
      setBusy(false);
    }
  }, []);

  const stop = useCallback(async () => {
    setBusy(true);
    try {
      await stopSwitchOverlay();
      setOverlay(null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to exit overlay');
      throw err;
    } finally {
      setBusy(false);
    }
  }, []);

  const up = useCallback(async () => {
    setBusy(true);
    try {
      const result = await upSwitchOverlay();
      setOverlay(result.data?.overlay || null);
      setError(null);
      return result;
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to go up');
      throw err;
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <OverlayContext.Provider value={{ overlay, error, busy, refresh, start, stop, up }}>
      {children}
    </OverlayContext.Provider>
  );
}

export function useOverlay() {
  const context = useContext(OverlayContext);
  if (!context) {
    throw new Error('useOverlay must be used within an OverlayProvider');
  }
  return context;
}

export default OverlayContext;
